<script setup lang="ts">
import type {
  FindManyFilterGroup,
  FindManyFilterGroupItem,
  PageFilter,
} from '@rialtic/types'
import { useArrayFilter, useArrayMap } from '@vueuse/core'
import { computed, ref } from '#imports'
import type { FilterItemWithState } from '../../composables/filters'
import { useFiltersState } from '../../composables/filters'
import { useUrlParamsState } from '../../composables/urlParamsState'
import useSimpleSearch from '../../composables/useSimpleSearch'
import FloatingUi from '../FloatingUi.vue'
import ListItemCheckbox from '../list/ItemCheckbox.vue'
import ListItemRadio from '../list/ItemRadio.vue'
import Search from '../Search.vue'
import FilterChip from './Chip.vue'

const props = defineProps<{
  filter: FindManyFilterGroup
  search?: boolean
}>()

const { getUrlParams, deleteUrlParam } = useUrlParamsState()

const { filterChange } = useFiltersState()

const emit = defineEmits([
  'filter-all',
  'filter-change',
  'filter-clear',
  'error',
])

const activator = ref(null)
const open = ref(false)
const searchInput = ref('')

const filtersWithActiveState = useArrayMap(
  () => props.filter.items || [],
  (item) =>
    ({
      ...item,
      active: getUrlParams(props.filter.name).includes(item.value.toString()),
    }) as FilterItemWithState<FindManyFilterGroupItem>,
)

const activeFilterLabels = useArrayMap(
  useArrayFilter(filtersWithActiveState, ({ active }) => !!active),
  ({ label }) => label,
)

const selectedFilter = computed(() =>
  filtersWithActiveState.value.find(({ active }) => active),
)

const secondaryFilter = computed(() => selectedFilter.value?.childFilter)

const secondaryOptions = computed(
  () =>
    secondaryFilter.value?.items?.map(
      (childItem: FilterItemWithState<FindManyFilterGroupItem>) => ({
        ...childItem,
        selected: childItem.disabled
          ? childItem.selected
          : getUrlParams(secondaryFilter.value!.name).includes(childItem.value),
      }),
    ) ?? [],
)

const filteredSecondaryOptions = useSimpleSearch(
  secondaryOptions,
  searchInput,
  ['label', 'value'],
)

const setOpen = (isOpen = false) => {
  open.value = isOpen
  if (!isOpen) searchInput.value = ''
}

const handleFilterChange = (
  item: FilterItemWithState<FindManyFilterGroupItem>,
  name: string,
  isMultiple = true,
  isChildItem = false,
) => {
  // Clear secondary filter and search when switching from a filter with child options
  if (selectedFilter?.value?.childFilter && !isChildItem) {
    deleteUrlParam(selectedFilter.value!.childFilter.name)
    searchInput.value = ''
  }

  filterChange(item, name, isMultiple, isChildItem)
}
</script>

<template>
  <div class="relative inline-block">
    <div ref="activator">
      <slot v-bind="{ open, setOpen }" name="activator">
        <FilterChip
          v-model="activeFilterLabels"
          class="h-full"
          :label="filter.label"
          button-border-radius="rounded-lg"
          data-testid="DropDownGroup__filterChip"
          @click="setOpen(true)"
        />
      </slot>
    </div>
    <FloatingUi
      v-if="open"
      class="bg-surface shadow-lg"
      :anchor="activator!"
      placement="bottom-start"
      data-testid="DropDownGroup__dropdown"
      @click-outside="open = false"
    >
      <template #default="{ maxDimensions }">
        <section class="min-w-54 flex flex-col" :style="maxDimensions">
          <header class="mx-3 border-b border-neutral-200">
            <div
              class="flex w-full items-center justify-between space-x-4 py-3"
            >
              <h3 class="h3 whitespace-nowrap text-neutral-900">
                {{ filter.label }}
              </h3>
              <button
                class="rounded-full p-1"
                aria-label="Close"
                data-testid="DropDownGroup__btnClose"
                @click="open = false"
              >
                <div class="i-ph-x-bold" />
              </button>
            </div>
          </header>
          <div>
            <h4
              v-if="filter.parentLabel"
              class="px-3 pt-2 text-sm font-medium"
              data-testid="DropDownGroup__parentLabel"
            >
              {{ filter.parentLabel }}
            </h4>
            <ul class="px-3 pb-3">
              <li v-for="item in filtersWithActiveState" :key="item.value">
                <ListItemRadio
                  :checked="!!item.active"
                  :data-testid="
                    'DropDownGroup__filterItem-' +
                    item.label.replaceAll(' ', '-').toLowerCase()
                  "
                  @click="
                    handleFilterChange(item, filter.name, filter.isMultiple)
                  "
                >
                  {{ item.label }}
                </ListItemRadio>
              </li>
            </ul>
          </div>
          <div
            v-if="secondaryFilter"
            class="flex-1"
            data-testid="DropDownGroup__secondaryFilter"
          >
            <h4 class="px-3 pt-2 text-sm font-medium">
              {{ secondaryFilter.label }}
            </h4>
            <div
              v-if="search"
              class="flex items-center justify-between px-3 py-2"
              data-testid="DropDownGroup__search"
            >
              <Search
                v-model="searchInput"
                :focus="open"
                placeholder="Search..."
                type="search"
              />
            </div>
            <ul class="max-h-[30vh] overflow-y-auto px-3 pb-3">
              <li
                v-for="item in search
                  ? filteredSecondaryOptions
                  : secondaryOptions"
                :key="item.value"
              >
                <ListItemCheckbox
                  :checked="item.selected"
                  :disabled="item.disabled"
                  :data-testid="
                    'DropDownGroup__secondaryFilterItem-' +
                    item.label.replaceAll(' ', '-').toLowerCase()
                  "
                  @click="
                    handleFilterChange(
                      item,
                      secondaryFilter!.name,
                      secondaryFilter!.isMultiple,
                      true,
                    )
                  "
                >
                  {{ item.label }}
                </ListItemCheckbox>
              </li>
            </ul>
          </div>
        </section>
      </template>
    </FloatingUi>
  </div>
</template>
